import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AuditionDetailSidebar.module.css';
import Icon from '../../components/common/Icon/Icon';
import { useAuth } from '../../contexts/AuthContext';
import { useSavedItems } from '../../contexts/SavedItemsContext';
import RoleOnlyModal from '../../shared/components/RoleOnlyModal';
import BoardPickerModal, { SavedConfirmModal } from '../../shared/components/BoardPickerModal';

const NOTICE_MESSAGE = '지원 후 프로필 정보가 제작사에 전달됩니다. 허위 정보 기재 시 불이익이 있을 수 있습니다.';

function getDDayColor(dDay) {
  if (!dDay) return '#FFA40D';
  if (/^d-?day$/i.test(dDay)) return '#EF4444';
  const match = dDay.match(/-?(\d+)/);
  if (!match) return '#FFA40D';
  const days = parseInt(match[1], 10);
  if (days <= 3) return '#EF4444';
  if (days <= 7) return '#F97316';
  return '#FFA40D';
}

function BookmarkIcon({ isSaved }) {
  if (isSaved) {
    return (
      <svg width="18" height="20" viewBox="0 0 18 20" fill="currentColor" aria-hidden="true">
        <path d="M3 1h12a2 2 0 0 1 2 2v15.5l-8-4.5-8 4.5V3a2 2 0 0 1 2-2z" />
      </svg>
    );
  }
  return (
    <svg width="18" height="20" viewBox="0 0 18 20" fill="none" aria-hidden="true">
      <path d="M3 1h12a2 2 0 0 1 2 2v15.5l-8-4.5-8 4.5V3a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AuditionDetailSidebar({ data, hasApplied, isExpired, onApply }) {
  const navigate = useNavigate();
  const { isLoggedIn, role, isTalent, isProducer, switchRole } = useAuth();
  const { castingMap, toggleSave, boardPickerState, savedConfirmState, selectBoard, createAndSelectBoard, closeBoardPicker, closeSavedConfirm } = useSavedItems();
  const isSaved = Boolean(castingMap[String(data.id)]);
  const [modalState, setModalState] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);

  const dDayColor = getDDayColor(data.dDay);
  const isDisabled = hasApplied || isExpired;
  const buttonLabel = hasApplied ? '지원 완료' : isExpired ? '지원 마감' : '지원하기';
  const applyButtonClass = [
    styles.applyButton,
    hasApplied ? styles.appliedButton : '',
    isExpired && !hasApplied ? styles.expiredButton : '',
  ].filter(Boolean).join(' ');

  const handleSaveToggle = () => {
    if (role !== 'TALENT') {
      setModalState({
        title: '배우 계정 전용 기능',
        message: isLoggedIn ? '공고 저장은 배우 계정에서만 이용할 수 있습니다.' : '배우로 로그인해서 이용해주세요.',
      });
      return;
    }
    toggleSave(data.id, 'CASTING_POST');
  };

  const handleShare = async () => {
    const copyLink = () => {
      navigator.clipboard?.writeText(window.location.href).then(() => {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      });
    };

    if (navigator.share) {
      try {
        await navigator.share({ title: data.title, url: window.location.href });
      } catch (err) {
        if (err.name !== 'AbortError') copyLink();
      }
    } else {
      copyLink();
    }
  };

  return (
    <>
      {modalState && (
        <RoleOnlyModal
          title={modalState.title}
          message={modalState.message}
          onClose={() => setModalState(null)}
          hasBothRoles={isLoggedIn && isTalent && isProducer}
          onSwitch={() => { setModalState(null); switchRole('TALENT'); window.location.href = '/talent/dashboard'; }}
          switchLabel="배우로 계정 전환하기"
          onboardingPath={isLoggedIn && !isTalent ? '/onboarding/talent' : undefined}
          onboardingLabel="배우 계정 만들러 가기"
        />
      )}
      {boardPickerState && (
        <BoardPickerModal
          boards={boardPickerState.options}
          targetType={boardPickerState.targetType}
          onClose={closeBoardPicker}
          onSelect={selectBoard}
          onCreateAndSelect={createAndSelectBoard}
        />
      )}
      {savedConfirmState && (
        <SavedConfirmModal
          boardName={savedConfirmState.boardName}
          onClose={closeSavedConfirm}
          onNavigateToArchive={() => {
            try { localStorage.setItem('talent-archive-open-board', String(savedConfirmState.boardId)); } catch {}
            closeSavedConfirm();
            navigate('/talent/archive');
          }}
        />
      )}
      <aside className={styles.sidebar}>
        <div className={styles.card}>
          {/* 정보 */}
          <div className={styles.infoSection}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>출연료</span>
              <span className={styles.infoValue}>{data.payment}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>촬영 기간</span>
              <span className={styles.infoValue}>{data.shootingPeriod}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>촬영 장소</span>
              <span className={styles.infoValue}>{data.shootingLocation}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>마감일</span>
              <div className={styles.deadlineValue}>
                {!isExpired && <span className={styles.dDay} style={{ color: dDayColor }}>{data.dDay}</span>}
                <span className={styles.infoValue}>{data.deadline}</span>
              </div>
            </div>
            {data.viewCount != null && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>조회수</span>
                <span className={styles.infoValue}>{data.viewCount.toLocaleString()}회</span>
              </div>
            )}
          </div>

          {/* 액션 버튼 */}
          <div className={styles.actions}>
            <button className={applyButtonClass} onClick={onApply} disabled={isDisabled}>
              {buttonLabel}
            </button>
            <div className={styles.secondaryRow}>
              <button
                className={`${styles.secondaryButton} ${isSaved ? styles.savedButton : ''}`}
                onClick={handleSaveToggle}
              >
                <BookmarkIcon isSaved={isSaved} />
                <span>{isSaved ? '저장됨' : '저장'}</span>
              </button>
              <button className={styles.secondaryButton} onClick={handleShare} style={{ position: 'relative' }}>
                <Icon name="share" size="18" />
                <span>공유</span>
                {shareCopied && (
                  <span style={{ position: 'absolute', top: '-34px', left: '50%', transform: 'translateX(-50%)', background: '#222', color: '#fff', fontSize: '12px', fontFamily: 'Pretendard, sans-serif', padding: '5px 10px', borderRadius: '6px', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 10 }}>
                    링크 복사됨
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* 주의사항 */}
        <div className={styles.notice}>
          <div className={styles.noticeHeader}>
            <span className={styles.noticeIcon} aria-hidden="true">ⓘ</span>
            <strong className={styles.noticeTitle}>주의사항</strong>
          </div>
          <p className={styles.noticeMessage}>{NOTICE_MESSAGE}</p>
        </div>
      </aside>
    </>
  );
}

export default AuditionDetailSidebar;
